import type { NewsOverviewItemData } from '@beat-venues/common-types';
import { ContentfulImage } from '@beat-venues/contentful-image';
import { useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { type VariantProps, tv } from 'tailwind-variants';

import { Heading, Paragraph } from '../../atoms';
import {
    type ButtonOrLinkProps,
    ButtonOrLink,
} from '../../atoms/button-or-link';
import type { CommonProps } from '../../types';
import { LogoTag } from '../logo-tag';

export interface NewsCardProps
    extends CommonProps,
        VariantProps<typeof NewsCardStyles>,
        Pick<ButtonOrLinkProps, 'href' | 'locale'>,
        Omit<NewsOverviewItemData, 'slug' | 'id'> {
    heading?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const NewsCardStyles = tv({
    slots: {
        root: 'relative group/newscard click-area',
        imageWrapper: 'flex overflow-hidden rounded-md rounded-tl-xl',
        image: 'h-full w-full object-cover transition group-hover/newscard:scale-105',
        logo: 'absolute -left-2 -top-2',
    },
    variants: {
        size: {
            lg: {
                imageWrapper: '',
                image: 'md:aspect-banner md:object-cover',
                logo: 'lg:-top-4 lg:-left-4',
            },
            md: {
                logo: 'lg:-top-4 lg:-left -4',
            },
            sm: '',
        },
    },
    defaultVariants: { size: 'sm' },
});

export const NewsCard = ({
    className,
    title,
    image,
    date,
    href,
    locale,
    venueId,
    size,
    heading,
    testId = 'news-card',
}: NewsCardProps): JSX.Element => {
    const { t } = useTranslation();

    const [venueName, setVenueName] = useState('');

    const { root, image: imageStyles, logo, imageWrapper } = NewsCardStyles();

    useEffect(() => {
        const venueTest = t(`venues.${venueId}.name`, 'global');
        setVenueName(venueTest);
    }, [t, venueId]);

    const getLogoSize = () => {
        switch (size) {
            case 'lg':
                return 'xl';

            case 'md':
                return 'lg';

            default:
                return 'md';
        }
    };

    return (
        <div className={root({ className })} data-testid={testId}>
            <div className={imageWrapper()}>
                {image && image.fields.image.fields.file ? (
                    <ContentfulImage
                        alt={image && image.fields.altText}
                        aspectRatio={size !== 'lg' ? 'BANNER' : undefined}
                        className={imageStyles({ size })}
                        src={image.fields.image.fields.file.url}
                    />
                ) : (
                    <div className="aspect-banner object-cover"></div>
                )}
            </div>

            <div className="flex flex-grow flex-col justify-between">
                <LogoTag
                    className={logo({ size })}
                    size={getLogoSize()}
                    venueId={venueId}
                />

                <div>
                    {venueName && (
                        <Paragraph className="mb-0 mt-3 uppercase">
                            {venueName}
                        </Paragraph>
                    )}
                    {title && (
                        <ButtonOrLink
                            className="click-area-link"
                            enableDrag={false}
                            href={href}
                            locale={locale}
                        >
                            <Heading
                                as={heading ?? 'h2'}
                                className="mb-0 mt-1 line-clamp-2 text-left group-hover/newscard:underline md:h-12"
                                size="h5"
                            >
                                {title}
                            </Heading>
                        </ButtonOrLink>
                    )}
                    {date && (
                        <Paragraph className="mb-0 mt-1.5">{date}</Paragraph>
                    )}
                </div>
            </div>
        </div>
    );
};
